import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export interface IProjectFilters {
  status: string[];
  dateFrom: string;
  dateTo: string;
  segment: string[];
}

interface ProjectFilterDialogProps {
  open: boolean;
  onClose: () => void;
  filters: IProjectFilters;
  onApply: (filters: IProjectFilters) => void;
}

const statusOptions = [
  { value: "Em Desenvolvimento", label: "Em Desenvolvimento" },
  { value: "Ativo", label: "Ativo" },
  { value: "Concluído", label: "Concluído" },
  { value: "Cancelado", label: "Cancelado" },
];

const segmentOptions = [
  { value: "Ficção", label: "Ficção" },
  { value: "Documentário", label: "Documentário" },
  { value: "Animação", label: "Animação" },
  { value: "Série", label: "Série" },
  { value: "Curta-metragem", label: "Curta-metragem" },
];

export default function ProjectFilterDialog({
  open,
  onClose,
  filters,
  onApply,
}: ProjectFilterDialogProps) {
  const [localFilters, setLocalFilters] = useState<IProjectFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, open]);

  const handleStatusToggle = (status: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }));
  };

  const handleSegmentToggle = (segment: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      segment: prev.segment.includes(segment)
        ? prev.segment.filter((s) => s !== segment)
        : [...prev.segment, segment],
    }));
  };

  const handleApply = () => {
    onApply(localFilters);
  };

  const handleClear = () => {
    const clearedFilters: IProjectFilters = {
      status: [],
      dateFrom: "",
      dateTo: "",
      segment: [],
    };
    setLocalFilters(clearedFilters);
    onApply(clearedFilters);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-darkblue border-gray-600 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-lg font-semibold">
            Filtros
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Status</p>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`status-${option.value}`}
                  checked={localFilters.status.includes(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Segment Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Segmento</p>
            <div className="space-y-2">
              {segmentOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`segment-${option.value}`}
                  checked={localFilters.segment.includes(option.value)}
                  onCheckedChange={() => handleSegmentToggle(option.value)}
                  label={option.label}
                />
              ))}
            </div>
          </div>

          {/* Date Range Filters */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Período</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="dateFrom" className="text-xs text-gray-400 mb-1 block">
                  De
                </label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
              <div>
                <label htmlFor="dateTo" className="text-xs text-gray-400 mb-1 block">
                  Até
                </label>
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                  }
                  className="bg-[#0C1427] border-gray-600 text-white"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleClear} className="flex-1">
            Limpar
          </Button>
          <Button onClick={handleApply} className="flex-1">
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
